import { MenuItem } from 'primeng/api';
import { Role } from '../enums/role.enum';
import { PrivFlag } from '../enums/priv-flag.enum';

// 客製選單物件
export interface AppMenuItem extends MenuItem {
  privFlag: PrivFlag[];
  role: Role[];
}
export class HeaderCondition {
  queryType?: number;
  displayRule?: number;
  ouId?: number;
  brand?: string;
  itemNumber?: string;
  itemMatchType?: string;
  vendorItem?: string;
  vendorItemMatchType?: string;
  ssCode?: string;
  quantityConditions?: any;
}

export class DetailCondition {
  uuid?: string;
  ouId?: number;
  inventoryItemId?: number;
  itemNumber?: string;
  vendorItem?: string;
  brand?: string;

  constructor(header: StockHeader, uuid: string) {
    this.uuid = uuid;
    this.ouId = header.ouId;
    this.inventoryItemId = header.inventoryItemId;
    this.itemNumber = header.itemNumber;
    this.vendorItem = header.vendorItem;
    this.brand = header.brand;
  }
}

export class DateCodeInquiryCondition {
  uuid?: string;
  ouId?: number;
  ouFullName?: string;
  itemNumber?: string;

  // 建構子重載
  constructor(header: StockHeader, uuid: string, ouFullName: string);
  constructor(ouId: number, ouFullName: string, itemNumber: string);

  // 實作
  constructor(param1: StockHeader | number, param2: string, param3: string) {
    if (typeof param1 === 'object') {
      // header: StockHeader, uuid: string, ouFullName: string
      const header = param1 as StockHeader;
      this.uuid = param2;
      this.ouId = header.ouId;
      this.ouFullName = param3;
      this.itemNumber = header.itemNumber;
    } else {
      // ouId: number, ouFullName: string, itemNumber: string
      this.ouId = param1;
      this.ouFullName = param2;
      this.itemNumber = param3;
    }
  }
}

export class AlternativeHeaderCondition {
  uuid?: string;
  ouId?: number;
  itemNumber?: string;
  vendorItem?: string;

  //itemNumber、vendorItem 無法同時傳入後端API，所以這裡只需要傳itemNumber即可
  constructor(uuid: string, header: StockHeader) {
    this.uuid = uuid;
    this.ouId = header.ouId;
    this.itemNumber = header.itemNumber;
  }
}

export class StockAgeInquiryCondition {
  uuid?: string;
  ouId?: number;
  inventoryItemId?: number;
  itemNumber?: string;
  queryType?: number;

  constructor(header: StockHeader, uuid: string, type: number) {
    this.uuid = uuid;
    this.ouId = header.ouId;
    this.inventoryItemId = header.inventoryItemId;
    this.itemNumber = header.itemNumber;
    this.queryType = type;
  }
}

export class StockAllocationCondition {
  uuid?: string;
  ouId?: number;
  inventoryItemId?: number;
  itemNumber?: string;
  queryType?: number;

  constructor(header: StockHeader, uuid: string, type: number) {
    this.uuid = uuid;
    this.ouId = header.ouId;
    this.inventoryItemId = header.inventoryItemId;
    this.itemNumber = header.itemNumber;
    this.queryType = type;
  }
}

/**
 * 客戶訂單查詢條件
 */
export class CustomerOrderInquiryCondition {
  uuid?: string;
  ouId?: number; // OUCode
  inventoryItemId?: number; // Item Number
  itemNumber?: string;
  queryType?: number;
  empNo?: string; // 當前登入者的 username (工號)
  type?: string; // 固定帶 "1"
  currency?: string; // 幣別，預設為空
  language?: string; // 語系，繁/簡中傳 "ZHT"，英文傳 "US"

  constructor(header: StockHeader, uuid: string, type: number) {
    this.uuid = uuid;
    this.ouId = header.ouId;
    this.inventoryItemId = header.inventoryItemId;
    this.itemNumber = header.itemNumber;
    this.queryType = type;
  }
}

export class GroupStockCondition {
  uuid?: string;
  ouId?: number;
  inventoryItemId?: number;
  itemNumber?: string;
  queryType?: number;

  constructor(header: StockHeader, uuid: string, type: number) {
    this.uuid = uuid;
    this.ouId = header.ouId;
    this.inventoryItemId = header.inventoryItemId;
    this.itemNumber = header.itemNumber;
    this.queryType = type;
  }
}

export class QuantityCondition {
  op!: string;
  value!: number;
}

export class Response {
  queryId!: string;
  responseData!: any;
  exceptionMsg!: string;
  failure!: boolean;
}

export class HeaderResponseData {
  stockHeaderList!: StockHeader[];
}

export class StockHeader {
  uuid!: string;
  ouId!: number;
  itemNumber?: string;
  ssCode?: string;
  vendorItem?: string;
  itemRefCode?: string;
  freeStkQty?: string;
  totStkQty?: string;
  lockQty?: string;
  pbkQty?: string;
  adQty?: string;
  shareStkQty?: string;
  inTransitQty?: string;
  sampleQty?: string;
  irQty?: string;
  brand?: string;
  privFlag?: string;
  inventoryItemId!: number;
}

export class StockDetail {
  ssCode?: string;
  itemRefCode?: string;
  uom?: string;
  vendorCtg?: string;
  itemSpecZht?: string;
  itemSpecUs?: string;
  itemDescZht?: string;
  itemDescUs?: string;
  itemStatus?: string;
  ncnr?: string;
  single?: string;
  customize?: string;
  promotion?: string;
  military?: string;
  snd?: string;
  pbitem?: string;
  allocation?: string;
  leadtime?: string;
  exportControl?: string;
  eccn?: string;
  ccats?: string;
  proviso?: string;
  pbstock?: string;
  reschedule?: string;
  rescheduleDay?: string;
  cancel?: string;
  cancelDay?: string;
  handleCtg?: string;
  stockStatus?: string;
  remark?: string;
  molq?: string;
  nwmg?: string;
  itemGroup?: string;
  vendorVmiItem?: string;
  msl?: string;
  pcnNo?: string;
  vendorLastOrderDate?: string;
  ncnrDate?: string;
  pcnIssueDate?: string;
  recommendedReplacement?: string;
  lastShipDate?: string;
  uuid?: string;
  inventoryItemId?: number;
  itemNumber?: string;
  hsCode?: string;
}

export class ItemShareLockInquiryCondition {
  uuid?: string;
  ouId?: number;
  inventoryItemId?: number;
  itemNumber?: string;
  queryType?: string;
  privFlag?: string;


  // 建構子重載
  constructor(header: StockHeader, uuid: string, queryType: string);
  constructor(ouId: number, itemNumber: string, queryType: string);

  // 實作
  constructor(param1: StockHeader | number, param2: string, param3: string) {
    if (typeof param1 === 'object') {
      // header: StockHeader, uuid: string, ouFullName: string
      const header = param1 as StockHeader;
      this.uuid = param2;
      this.ouId = header.ouId;
      this.inventoryItemId = header.inventoryItemId;
      this.itemNumber = header.itemNumber;
      this.queryType = param3;
      this.privFlag = header.privFlag;
    } else {
      // ouId: number, itemNumber: string
      this.ouId = param1;
      this.itemNumber = param2;
    }
  }
}

export class PbkDetailsCondition {
  uuid?: string;
  ouId?: number;
  inventoryItemId?: number;
  itemNumber?: string;
  queryType?: number;
  privFlag?: string;

  constructor(header: StockHeader, uuid: string, type: number) {
    this.uuid = uuid;
    this.ouId = header.ouId;
    this.inventoryItemId = header.inventoryItemId;
    this.itemNumber = header.itemNumber;
    this.queryType = type;
    this.privFlag = header.privFlag;
  }
}

export class AdvisingCondition {
  uuid?: string;
  ouId?: number;
  inventoryItemId?: number;
  itemNumber?: string;
  queryType?: number;

  constructor(header: StockHeader, uuid: string, type: number) {
    this.uuid = uuid;
    this.ouId = header.ouId;
    this.inventoryItemId = header.inventoryItemId;
    this.itemNumber = header.itemNumber;
    this.queryType = type;
  }
}

export class IntransitDetailCondition {
  uuid?: string;
  ouId?: number;
  inventoryItemId?: number;
  itemNumber?: string;
  queryType?: number;

  constructor(header: StockHeader, uuid: string, type: number) {
    this.uuid = uuid;
    this.ouId = header.ouId;
    this.inventoryItemId = header.inventoryItemId;
    this.itemNumber = header.itemNumber;
    this.queryType = type;
  }
}

export class WHInvBookkeepingSearchCondition {
  uuid?: string;
  ouId?: number;
  inventoryItemId?: number;
  itemNumber?: string;
  privFlag?: string;
  beginMonth?: string;
  endMonth?: string;


  // 建構子重載
  constructor(header: StockHeader, uuid: string, beginMonth: string, endMonth: string);
  constructor(ouId: number, itemNumber: string, beginMonth: string, endMonth: string);

  // 實作
  constructor(param1: StockHeader | number, param2: string, param3: string, param4: string) {
    if (typeof param1 === 'object') {
      // header: StockHeader, uuid
      const header = param1 as StockHeader;
      this.uuid = param2;
      this.beginMonth = param3;
      this.endMonth = param4;
      this.ouId = header.ouId;
      this.inventoryItemId = header.inventoryItemId;
      this.itemNumber = header.itemNumber;
      this.privFlag = header.privFlag;
    } else {
      // ouId: number, itemNumber: string
      this.ouId = param1;
      this.itemNumber = param2;
      this.beginMonth = param3;
      this.endMonth = param4;
    }
  }
}